import { Box, Tooltip, Typography } from '@mui/material';
import React, { useCallback, useMemo, useRef, useState } from 'react';

export interface CameraRecording {
  channelId: number;
  channelNumber: number;
  startedAt: string;
  endedAt: string;
  fileSize: number;
  storeType: number;
  streamType?: number;
}

interface CameraPlaybackTimelineProps {
  recordings: CameraRecording[];
  rangeStart: Date;
  rangeEnd: Date;
  currentTime?: Date | null;
  activeRecording?: CameraRecording | null;
  disabled?: boolean;
  variant?: 'default' | 'overlay';
  timeZoneOffsetMinutes: number;
  channelLabels?: Record<number, string>;
  onSelect: (recording: CameraRecording, timestamp: Date) => void;
}

const LABEL_WIDTH = 36; // px — "CH X" label column
const RULER_HEIGHT = 16; // px — time ticks row
const ROW_HEIGHT = 18; // px — per channel row

const OVERLAY_ROW_HEIGHT = 6; // px — altura reservada (a barra cresce dentro dela)
const OVERLAY_BAR_IDLE = 3; // px — espessura da barra em repouso
const OVERLAY_BAR_HOVER = 5; // px — espessura da barra no hover
const OVERLAY_KNOB_SIZE = 12; // px — diâmetro do marcador de posição

const OVERLAY_TRACK_COLOR = 'rgba(255, 255, 255, 0.26)';
const OVERLAY_SEGMENT_COLOR = 'rgba(255, 255, 255, 0.62)';
const OVERLAY_SEGMENT_PARTIAL_COLOR = 'rgba(255, 255, 255, 0.36)';
const OVERLAY_PLAYED_COLOR = '#ff0000';
const OVERLAY_TICK_COLOR = 'rgba(255, 255, 255, 0.14)';

// Formata sempre no fuso do dispositivo: desloca o instante pelo offset e fixa o timeZone em UTC.
const formatTimeInZone = (value: Date, options: Intl.DateTimeFormatOptions, offsetMinutes: number) =>
  new Intl.DateTimeFormat('pt-BR', { ...options, timeZone: 'UTC' }).format(new Date(value.getTime() + offsetMinutes * 60_000));

const formatTime = (value: Date, offsetMinutes: number) => formatTimeInZone(value, { hour: '2-digit', minute: '2-digit' }, offsetMinutes);

const formatTimeWithSeconds = (value: Date, offsetMinutes: number) =>
  formatTimeInZone(value, { hour: '2-digit', minute: '2-digit', second: '2-digit' }, offsetMinutes);

const formatDuration = (startMs: number, endMs: number) => {
  const totalSeconds = Math.max(0, Math.round((endMs - startMs) / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  return hours > 0 ? `${hours}h ${String(minutes).padStart(2, '0')}m` : `${minutes}m ${String(seconds).padStart(2, '0')}s`;
};

const clamp = (value: number, minimum: number, maximum: number) => Math.max(minimum, Math.min(maximum, value));

const parseTime = (value: string) => {
  const timestamp = new Date(value).getTime();
  return Number.isFinite(timestamp) ? timestamp : null;
};

const getTickCount = (durationMs: number): number => {
  const hours = durationMs / 3_600_000;
  if (hours <= 1) return 4;
  if (hours <= 3) return 6;
  if (hours <= 6) return 8;
  return 6;
};

export const CameraPlaybackTimeline: React.FC<CameraPlaybackTimelineProps> = ({
  recordings,
  rangeStart,
  rangeEnd,
  currentTime,
  activeRecording,
  disabled = false,
  variant = 'default',
  timeZoneOffsetMinutes,
  channelLabels = {},
  onSelect,
}) => {
  const [hoverRatio, setHoverRatio] = useState<number | null>(null);
  const didDragRef = useRef(false);
  const dragRef = useRef<{ startX: number } | null>(null);

  const rangeStartMs = rangeStart.getTime();
  const rangeEndMs = rangeEnd.getTime();
  const rangeDurationMs = rangeEndMs - rangeStartMs;

  // All layout is in % — no fixed pixel width, no scroll
  const timeline = useMemo(() => {
    if (!Number.isFinite(rangeStartMs) || !Number.isFinite(rangeEndMs) || rangeDurationMs <= 0) {
      return { channels: [], segments: [], ticks: [], totalHeight: RULER_HEIGHT };
    }

    const channels = Array.from(new Set(recordings.map(r => r.channelNumber))).sort((a, b) => a - b);

    const segments = recordings
      .map(recording => {
        const recStartMs = parseTime(recording.startedAt);
        const recEndMs = parseTime(recording.endedAt);
        if (recStartMs === null || recEndMs === null || recEndMs <= recStartMs) return null;

        const visibleStartMs = Math.max(rangeStartMs, recStartMs);
        const visibleEndMs = Math.min(rangeEndMs, recEndMs);
        if (visibleEndMs <= visibleStartMs) return null;

        const channelIndex = channels.indexOf(recording.channelNumber);
        const leftPct = ((visibleStartMs - rangeStartMs) / rangeDurationMs) * 100;
        const widthPct = Math.max(0.2, ((visibleEndMs - visibleStartMs) / rangeDurationMs) * 100);

        return {
          recording,
          channelIndex,
          leftPct,
          widthPct,
          recordingStartMs: recStartMs,
          recordingEndMs: recEndMs,
          visibleStartMs,
          visibleEndMs,
        };
      })
      .filter((s): s is NonNullable<typeof s> => s !== null);

    const tickCount = getTickCount(rangeDurationMs);
    const ticks: { ratio: number; label: string }[] = [];
    for (let i = 1; i < tickCount; i++) {
      const ratio = i / tickCount;
      ticks.push({ ratio, label: formatTime(new Date(rangeStartMs + ratio * rangeDurationMs), timeZoneOffsetMinutes) });
    }

    return {
      channels,
      segments,
      ticks,
      totalHeight: RULER_HEIGHT + Math.max(channels.length, 1) * ROW_HEIGHT,
    };
  }, [rangeDurationMs, rangeEndMs, rangeStartMs, recordings, timeZoneOffsetMinutes]);

  const currentRatio =
    currentTime && Number.isFinite(currentTime.getTime()) && rangeDurationMs > 0 ? clamp((currentTime.getTime() - rangeStartMs) / rangeDurationMs, 0, 1) : null;

  // União da cobertura de todos os canais, com os canais gravando em cada trecho.
  const coverage = useMemo(() => {
    if (!timeline.segments.length || rangeDurationMs <= 0) return [];

    const boundarySet = new Set<number>();
    timeline.segments.forEach(segment => {
      boundarySet.add(segment.visibleStartMs);
      boundarySet.add(segment.visibleEndMs);
    });
    const boundaries = Array.from<number>(boundarySet).sort((a, b) => a - b);

    const intervals: { startMs: number; endMs: number; channelNumbers: number[] }[] = [];
    for (let index = 0; index < boundaries.length - 1; index += 1) {
      const startMs = boundaries[index];
      const endMs = boundaries[index + 1];
      if (endMs <= startMs) continue;

      const middleMs = startMs + (endMs - startMs) / 2;
      const channelNumbers = Array.from(
        new Set(
          timeline.segments
            .filter(segment => middleMs >= segment.visibleStartMs && middleMs <= segment.visibleEndMs)
            .map(segment => segment.recording.channelNumber)
        )
      ).sort((a, b) => a - b);
      if (!channelNumbers.length) continue;

      const channelKey = channelNumbers.join('|');
      const previousChannelKey = intervals[intervals.length - 1]?.channelNumbers.join('|');
      const previous = intervals[intervals.length - 1];
      if (previous && previous.endMs === startMs && previousChannelKey === channelKey) {
        previous.endMs = endMs;
      } else {
        intervals.push({ startMs, endMs, channelNumbers });
      }
    }

    return intervals.map(interval => ({
      ...interval,
      channelCount: interval.channelNumbers.length,
      channelLabel: interval.channelNumbers.map(channelNumber => channelLabels[channelNumber] || `Câmera ${channelNumber}`).join(', '),
      leftPct: ((interval.startMs - rangeStartMs) / rangeDurationMs) * 100,
      widthPct: Math.max(0.2, ((interval.endMs - interval.startMs) / rangeDurationMs) * 100),
    }));
  }, [channelLabels, rangeDurationMs, rangeStartMs, timeline.segments]);

  const getTrackRatio = useCallback((event: React.PointerEvent<HTMLDivElement> | React.MouseEvent<HTMLDivElement>) => {
    const bounds = event.currentTarget.getBoundingClientRect();
    return clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
  }, []);

  const handleTrackPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    setHoverRatio(getTrackRatio(event));
    if (dragRef.current) {
      const distance = Math.abs(event.clientX - dragRef.current.startX);
      if (distance > 4) didDragRef.current = true;
    }
  };

  const handleTrackPointerLeave = () => {
    setHoverRatio(null);
    dragRef.current = null;
  };

  const handleTrackPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (disabled) return;
    dragRef.current = { startX: event.clientX };
    didDragRef.current = false;
  };

  const handleTrackPointerUp = () => {
    dragRef.current = null;
  };

  const selectSegmentPoint = (event: React.MouseEvent<HTMLDivElement>, segment: typeof timeline.segments[number]) => {
    if (disabled || didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    const bounds = event.currentTarget.getBoundingClientRect();
    const ratio = clamp((event.clientX - bounds.left) / bounds.width, 0, 1);
    const timestamp = new Date(segment.visibleStartMs + ratio * (segment.visibleEndMs - segment.visibleStartMs));
    onSelect(segment.recording, timestamp);
  };

  // No overlay o clique vale para a trilha inteira: procuramos qualquer gravação que cubra o
  // instante escolhido, dando preferência ao canal já ativo, e o consumidor resolve os demais.
  const selectPointOnTrack = (event: React.MouseEvent<HTMLDivElement>) => {
    if (disabled || didDragRef.current) {
      didDragRef.current = false;
      return;
    }
    const timestampMs = rangeStartMs + getTrackRatio(event) * rangeDurationMs;
    const candidates = timeline.segments.filter(segment => timestampMs >= segment.visibleStartMs && timestampMs <= segment.visibleEndMs);
    if (!candidates.length) return;
    const preferred = candidates.find(segment => segment.recording.channelId === activeRecording?.channelId) ?? candidates[0];
    onSelect(preferred.recording, new Date(timestampMs));
  };

  if (!recordings.length) return null;

  if (variant === 'overlay') {
    const isHovering = hoverRatio !== null && !disabled;
    const barHeight = isHovering ? OVERLAY_BAR_HOVER : OVERLAY_BAR_IDLE;
    const playedPct = currentRatio !== null ? currentRatio * 100 : null;
    const totalChannels = timeline.channels.length;
    const hoverPct = hoverRatio !== null ? hoverRatio * 100 : null;
    const hoverCoverage =
      hoverPct !== null ? coverage.find(interval => hoverPct >= interval.leftPct && hoverPct <= interval.leftPct + interval.widthPct) : undefined;

    return (
      <Box sx={{ width: '100%', userSelect: 'none', opacity: disabled ? 0.6 : 1, px: 1.5, boxSizing: 'border-box' }} aria-label="Timeline de gravações">
        <Box sx={{ position: 'relative', height: OVERLAY_ROW_HEIGHT, mt: '14px' }}>
          <Box
            onClick={selectPointOnTrack}
            onPointerDown={handleTrackPointerDown}
            onPointerMove={handleTrackPointerMove}
            onPointerUp={handleTrackPointerUp}
            onPointerCancel={handleTrackPointerUp}
            onPointerLeave={handleTrackPointerLeave}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: 0,
              height: '100%',
              cursor: disabled ? 'default' : 'pointer',
            }}
          >
            {timeline.ticks.map(tick => (
              <Box
                key={tick.ratio}
                sx={{
                  position: 'absolute',
                  left: `${tick.ratio * 100}%`,
                  top: 0,
                  height: '100%',
                  borderLeft: 1,
                  borderColor: OVERLAY_TICK_COLOR,
                  pointerEvents: 'none',
                }}
              />
            ))}

            <Box
              sx={{
                position: 'absolute',
                left: 0,
                right: 0,
                top: '50%',
                transform: 'translateY(-50%)',
                height: barHeight,
                borderRadius: 999,
                backgroundColor: OVERLAY_TRACK_COLOR,
                transition: 'height 120ms ease',
                pointerEvents: 'none',
              }}
            >
              {coverage.map(interval => {
                const intervalRight = interval.leftPct + interval.widthPct;
                const playedWidth = playedPct !== null && playedPct > interval.leftPct ? Math.min(intervalRight, playedPct) - interval.leftPct : 0;
                const isFullCoverage = interval.channelCount >= totalChannels;

                return (
                  <React.Fragment key={`${interval.startMs}-${interval.endMs}`}>
                    <Box
                      sx={{
                        position: 'absolute',
                        left: `${interval.leftPct}%`,
                        width: `${interval.widthPct}%`,
                        top: 0,
                        bottom: 0,
                        borderRadius: 999,
                        backgroundColor: isFullCoverage ? OVERLAY_SEGMENT_COLOR : OVERLAY_SEGMENT_PARTIAL_COLOR,
                      }}
                    />
                    {playedWidth > 0 && (
                      <Box
                        sx={{
                          position: 'absolute',
                          left: `${interval.leftPct}%`,
                          width: `${playedWidth}%`,
                          top: 0,
                          bottom: 0,
                          borderRadius: 999,
                          backgroundColor: OVERLAY_PLAYED_COLOR,
                          opacity: isFullCoverage ? 1 : 0.65,
                        }}
                      />
                    )}
                  </React.Fragment>
                );
              })}
            </Box>

            {isHovering && (
              <>
                <Box
                  sx={{
                    position: 'absolute',
                    left: `${hoverRatio * 100}%`,
                    top: 0,
                    height: '100%',
                    borderLeft: 1,
                    borderColor: 'rgba(255,255,255,0.7)',
                    pointerEvents: 'none',
                  }}
                />
                <Typography
                  sx={{
                    position: 'absolute',
                    left: `${hoverRatio * 100}%`,
                    top: -16,
                    transform: `translateX(${hoverRatio < 0.15 ? '0' : hoverRatio > 0.85 ? '-100%' : '-50%'})`,
                    px: 0.5,
                    py: 0.15,
                    borderRadius: 0.5,
                    backgroundColor: 'rgba(0,0,0,0.85)',
                    color: '#fff',
                    fontSize: 10,
                    lineHeight: 1.3,
                    fontVariantNumeric: 'tabular-nums',
                    whiteSpace: 'nowrap',
                    pointerEvents: 'none',
                  }}
                >
                  {formatTimeWithSeconds(new Date(rangeStartMs + hoverRatio * rangeDurationMs), timeZoneOffsetMinutes)}
                  {totalChannels > 1 && hoverCoverage && ` · ${hoverCoverage.channelLabel}`}
                  {totalChannels > 1 && !hoverCoverage && ' · sem gravação'}
                  {totalChannels <= 1 && !hoverCoverage && ' · sem gravação'}
                </Typography>
              </>
            )}

            {currentRatio !== null && (
              <Box
                sx={{
                  position: 'absolute',
                  left: `${currentRatio * 100}%`,
                  top: '50%',
                  width: OVERLAY_KNOB_SIZE,
                  height: OVERLAY_KNOB_SIZE,
                  transform: 'translate(-50%, -50%)',
                  borderRadius: '50%',
                  backgroundColor: OVERLAY_PLAYED_COLOR,
                  boxShadow: '0 0 0 1px rgba(0,0,0,0.35)',
                  pointerEvents: 'none',
                }}
              />
            )}
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', pt: '6px' }}>
          <Typography sx={{ fontSize: 10, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,0.92)' }}>
            {currentTime && Number.isFinite(currentTime.getTime()) ? formatTimeWithSeconds(currentTime, timeZoneOffsetMinutes) : '--:--:--'}
          </Typography>
          <Typography sx={{ fontSize: 10, lineHeight: 1.3, fontVariantNumeric: 'tabular-nums', color: 'rgba(255,255,255,0.6)' }}>
            {formatTime(rangeStart, timeZoneOffsetMinutes)} — {formatTime(rangeEnd, timeZoneOffsetMinutes)}
          </Typography>
        </Box>
      </Box>
    );
  }

  return (
    <Box
      sx={{
        width: '100%',
        height: timeline.totalHeight,
        position: 'relative',
        userSelect: 'none',
        opacity: disabled ? 0.72 : 1,
      }}
      aria-label="Timeline de gravações"
    >
      <Box
        sx={{
          position: 'absolute',
          left: 0,
          top: RULER_HEIGHT,
          width: LABEL_WIDTH,
          height: timeline.totalHeight - RULER_HEIGHT,
          pointerEvents: 'none',
        }}
      >
        {timeline.channels.map((channel, index) => (
          <Typography
            key={channel}
            sx={{
              position: 'absolute',
              top: index * ROW_HEIGHT,
              height: ROW_HEIGHT,
              width: '100%',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'flex-end',
              pr: 0.75,
              fontSize: 9,
              color: 'text.secondary',
              lineHeight: 1,
            }}
          >
            CH {channel}
          </Typography>
        ))}
      </Box>

      <Box
        onPointerDown={handleTrackPointerDown}
        onPointerMove={handleTrackPointerMove}
        onPointerUp={handleTrackPointerUp}
        onPointerCancel={handleTrackPointerUp}
        onPointerLeave={handleTrackPointerLeave}
        sx={{
          position: 'absolute',
          left: LABEL_WIDTH,
          right: 0,
          top: 0,
          height: timeline.totalHeight,
          cursor: disabled ? 'default' : 'crosshair',
        }}
      >
        <Box
          sx={{
            position: 'absolute',
            left: 0,
            right: 0,
            top: 0,
            height: RULER_HEIGHT,
            borderBottom: 1,
            borderColor: 'divider',
          }}
        >
          {timeline.ticks.map(tick => (
            <Box key={tick.ratio} sx={{ position: 'absolute', left: `${tick.ratio * 100}%`, top: 0, height: '100%' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: -(timeline.totalHeight - RULER_HEIGHT),
                  borderLeft: 1,
                  borderColor: 'divider',
                  opacity: 0.45,
                }}
              />
              <Typography
                sx={{
                  position: 'absolute',
                  top: 2,
                  left: 3,
                  fontSize: 9,
                  color: 'text.disabled',
                  whiteSpace: 'nowrap',
                  fontVariantNumeric: 'tabular-nums',
                  lineHeight: 1,
                }}
              >
                {tick.label}
              </Typography>
            </Box>
          ))}
        </Box>

        {timeline.channels.map((channel, index) => (
          <Box
            key={channel}
            sx={{
              position: 'absolute',
              left: 0,
              right: 0,
              top: RULER_HEIGHT + index * ROW_HEIGHT,
              height: ROW_HEIGHT,
              backgroundColor: index % 2 === 0 ? 'action.hover' : 'transparent',
              borderBottom: 1,
              borderColor: 'divider',
            }}
          />
        ))}

        {timeline.segments.map(segment => {
          const isActive =
            activeRecording?.channelId === segment.recording.channelId &&
            activeRecording?.startedAt === segment.recording.startedAt &&
            activeRecording?.endedAt === segment.recording.endedAt;

          const segTop = RULER_HEIGHT + segment.channelIndex * ROW_HEIGHT + 3;
          const segHeight = ROW_HEIGHT - 6;

          return (
            <Tooltip
              key={`${segment.recording.startedAt}-${segment.recording.endedAt}-${segment.recording.channelNumber}`}
              title={`Câmera ${segment.recording.channelNumber} · ${formatTimeWithSeconds(
                new Date(segment.recordingStartMs),
                timeZoneOffsetMinutes
              )} — ${formatTimeWithSeconds(new Date(segment.recordingEndMs), timeZoneOffsetMinutes)} · ${formatDuration(
                segment.recordingStartMs,
                segment.recordingEndMs
              )} · ${Math.round(segment.recording.fileSize / 1024 / 1024)} MB`}
            >
              <Box
                data-timeline-segment="true"
                role="button"
                tabIndex={disabled ? -1 : 0}
                onClick={event => selectSegmentPoint(event, segment)}
                onKeyDown={event => {
                  if (!disabled && (event.key === 'Enter' || event.key === ' ')) {
                    event.preventDefault();
                    onSelect(segment.recording, new Date(segment.recordingStartMs));
                  }
                }}
                sx={{
                  position: 'absolute',
                  left: `${segment.leftPct}%`,
                  width: `${segment.widthPct}%`,
                  top: segTop,
                  height: segHeight,
                  borderRadius: 0.5,
                  backgroundColor: isActive ? 'secondary.main' : 'primary.main',
                  border: 1,
                  borderColor: isActive ? 'secondary.dark' : 'primary.dark',
                  opacity: disabled ? 0.55 : 0.9,
                  cursor: disabled ? 'default' : 'pointer',
                  '&:hover': { opacity: disabled ? 0.55 : 1 },
                  '&:focus-visible': {
                    outline: '2px solid',
                    outlineColor: 'warning.main',
                    outlineOffset: 1,
                  },
                }}
              />
            </Tooltip>
          );
        })}

        {currentRatio !== null && currentTime && (
          <Box
            sx={{
              position: 'absolute',
              left: `${currentRatio * 100}%`,
              top: 0,
              height: timeline.totalHeight,
              borderLeft: 2,
              borderColor: 'error.main',
              zIndex: 4,
              pointerEvents: 'none',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -1,
                left: -4,
                width: 0,
                height: 0,
                borderLeft: '4px solid transparent',
                borderRight: '4px solid transparent',
                borderTop: '5px solid',
                borderTopColor: 'error.main',
              }}
            />
            <Typography
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                px: 0.4,
                py: 0.1,
                color: 'error.contrastText',
                backgroundColor: 'error.main',
                borderRadius: 0.5,
                whiteSpace: 'nowrap',
                fontSize: 9,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.4,
              }}
            >
              {formatTimeWithSeconds(currentTime, timeZoneOffsetMinutes)}
            </Typography>
          </Box>
        )}

        {hoverRatio !== null && !disabled && (
          <Box
            sx={{
              position: 'absolute',
              left: `${hoverRatio * 100}%`,
              top: 0,
              height: timeline.totalHeight,
              borderLeft: 1,
              borderColor: 'text.secondary',
              borderStyle: 'dashed',
              zIndex: 5,
              pointerEvents: 'none',
            }}
          >
            <Typography
              sx={{
                position: 'absolute',
                top: 4,
                left: 4,
                px: 0.4,
                py: 0.1,
                color: 'background.paper',
                backgroundColor: 'text.secondary',
                borderRadius: 0.5,
                whiteSpace: 'nowrap',
                fontSize: 9,
                fontWeight: 600,
                fontVariantNumeric: 'tabular-nums',
                lineHeight: 1.4,
              }}
            >
              {formatTimeWithSeconds(new Date(rangeStartMs + hoverRatio * rangeDurationMs), timeZoneOffsetMinutes)}
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
};
